<template src="./RiskControls.html"></template>

<script>
import { rampAccent } from "@/modules/DataAnalysis/Risk/riskFormat";

export default {
  name: "RiskControls",
  props: {
    presentation: { type: Object, default: null },
    threshold: { type: [Number, String], default: null },
    op: { type: String, default: null },
    opSelectable: { type: Boolean, default: false },
    // The selected Heat Index level; the levels themselves come from the presentation.
    category: { type: String, default: null },
    // The minimum daily exposure (N), for the indicator that takes one.
    minHours: { type: [Number, String], default: null },
  },
  data() {
    return {
      draft: this.threshold,
    };
  },
  computed: {
    // Same accent the chart and the period summary use (riskFormat.js#rampAccent)
    // — the active preset chip reads as the indicator's own color rather than a
    // fixed blue.
    accent() {
      return rampAccent(this.presentation && this.presentation.id);
    },
    presets() {
      return (this.presentation && this.presentation.presets) || null;
    },
    // Heat Index names its presets by their unit ("Hours per day"); the rest keep
    // the generic label.
    presetsLabel() {
      return (this.presentation && this.presentation.presetsLabel) || "Default threshold";
    },
    // Persistence takes two numbers: the threshold above is the run length (D); this
    // is the daily condition (N hours/day) each day of the run must meet.
    minHoursField() {
      const p = this.presentation;
      if (!p || !p.minHoursSelectable) return null;
      return {
        label: p.minHoursLabel || "Minimum hours per day",
        range: p.minHoursRange || { min: 1, max: 24, step: 1 },
      };
    },
    categories() {
      const p = this.presentation;
      return p && p.categorySelectable && p.categories && p.categories.length ? p.categories : null;
    },
    range() {
      return (this.presentation && this.presentation.thresholdRange) || { min: -20, max: 40, step: 1 };
    },
    label() {
      return (this.presentation && this.presentation.thresholdLabel) || "Threshold";
    },
    // A blank box still steps, from the committed value.
    stepBase() {
      const value = Number(this.draft === "" || this.draft == null ? this.threshold : this.draft);
      return Number.isNaN(value) ? Number(this.threshold) : value;
    },
    canStepUp() {
      return this.stepBase < this.range.max;
    },
    canStepDown() {
      return this.stepBase > this.range.min;
    },
  },
  watch: {
    threshold(value) {
      this.draft = value;
    },
    // Reset the draft to whatever this indicator's own threshold is when
    // the active indicator changes underneath this component.
    presentation() {
      this.draft = this.threshold;
    },
  },
  methods: {
    activePreset(value) {
      return Number(this.draft) === Number(value);
    },
    activeOp(value) {
      return value === this.op;
    },
    activeCategory(value) {
      return value === this.category;
    },
    // Committed on change (not per keystroke), clamped to the allowed range, and only
    // when it actually differs — the same contract as the threshold box.
    onMinHoursChange(event) {
      const { min, max } = this.minHoursField.range;
      const value = Number(event.target.value);
      if (event.target.value === "" || Number.isNaN(value)) {
        event.target.value = this.minHours;
        return;
      }
      const clamped = Math.min(max, Math.max(min, value));
      event.target.value = clamped;
      if (String(clamped) !== String(this.minHours)) this.$emit("min-hours", clamped);
    },
    onPresetClick(value) {
      this.draft = value;
      this.$emit("preset", value);
    },
    onInput(event) {
      this.draft = event.target.value;
    },
    // Steps the draft AND commits it — holding an arrow to walk from 28 to 33
    // still costs one request, not five, because the parent debounces "commit"
    // the same way it debounces typing (Risk.vue#debouncedCommitThreshold).
    // Rounded to the step so 0.5 + 0.05 lands on 0.55 rather than
    // 0.5500000000000001.
    step(direction) {
      const { min, max, step } = this.range;
      const next = this.stepBase + direction * step;
      const decimals = String(step).includes(".") ? String(step).split(".")[1].length : 0;
      this.draft = Number(Math.min(max, Math.max(min, next)).toFixed(decimals));
      this.commit();
    },
    // Parses and clamps the draft, writing the corrected value back into the box.
    // Returns null when the draft is not a number, having restored the last good one.
    resolveDraft() {
      const value = Number(this.draft);
      if (this.draft === "" || Number.isNaN(value)) {
        this.draft = this.threshold;
        return null;
      }
      const clamped = Math.min(this.range.max, Math.max(this.range.min, value));
      this.draft = clamped;
      return clamped;
    },
    // Fires on blur, Enter (via onKeydown) and the stepper arrows. Compared as
    // text, the same way the removed Load button's dirty check was, so a
    // resolved value equal to the current threshold — nothing typed, or a
    // clamp that landed back where it started — costs no request.
    commit() {
      const value = this.resolveDraft();
      if (value === null) return;
      if (String(value) === String(this.threshold)) return;
      this.$emit("commit", value);
    },
    onKeydown(event) {
      if (event.key === "Enter") {
        event.target.blur();
      }
    },
  },
};
</script>

<style lang="scss" src="./RiskControls.scss"></style>
